/**
 * What you can do to a layer, on right-click. §6.3.
 *
 * Everything here already had a keyboard shortcut and most of it had a button
 * somewhere; what it did not have was a place a customer would look. Right-click
 * is where people look, and grouping in particular was unreachable without
 * knowing that Ctrl+G existed.
 *
 * The menu shows the same items whatever it was opened from - the canvas or the
 * layers list - and greys out what does not apply rather than hiding it, so the
 * shape of the menu does not change under the pointer.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useEditor, useSelectedLayers } from '../state/store';
import {
  deleteSelected,
  duplicateSelected,
  groupSelected,
  reorderSelected,
  toggleLock,
  ungroupSelected,
} from '../state/actions';

export interface MenuAt {
  x: number;
  y: number;
}

interface Props {
  at: MenuAt;
  onClose: () => void;
  /** Opens a side panel - for the one kind of layer whose content lives there. */
  onPanel?: ( which: 'settings' ) => void;
}

export function LayerMenu( { at, onClose, onPanel }: Props ) {
  const selected = useSelectedLayers();
  const beginRenaming = useEditor( ( s ) => s.beginRenaming );
  const panel = useRef<HTMLDivElement>( null );
  const [ place, setPlace ] = useState( at );

  /*
   * Nudged back on screen if it would hang off the edge.
   *
   * Measured after it is laid out rather than guessed from a fixed height: the
   * menu is short, but a right-click near the bottom of a laptop screen is
   * exactly where it would otherwise open with its last item unreachable.
   */
  useLayoutEffect( () => {
    const box = panel.current?.getBoundingClientRect();

    if ( ! box ) return;

    const room = 8;

    setPlace( {
      x: Math.min( at.x, window.innerWidth - box.width - room ),
      y: Math.min( at.y, window.innerHeight - box.height - room ),
    } );
  }, [ at.x, at.y ] );

  useEffect( () => {
    const away = ( event: MouseEvent ) => {
      if ( panel.current && ! panel.current.contains( event.target as Node ) ) onClose();
    };

    const key = ( event: KeyboardEvent ) => {
      if ( event.key === 'Escape' ) onClose();
    };

    // Late, so the click that opened this does not close it again.
    const timer = window.setTimeout( () => window.addEventListener( 'mousedown', away ), 0 );

    window.addEventListener( 'keydown', key );
    window.addEventListener( 'resize', onClose );

    return () => {
      window.clearTimeout( timer );
      window.removeEventListener( 'mousedown', away );
      window.removeEventListener( 'keydown', key );
      window.removeEventListener( 'resize', onClose );
    };
  }, [ onClose ] );

  if ( selected.length === 0 ) return null;

  const many = selected.length > 1;
  const locked = selected.every( ( layer ) => layer.locked );
  const grouped = selected.some( ( layer ) => layer.type === 'group' );

  const run = ( work: () => void ) => () => {
    work();
    onClose();
  };

  const items: Array<
    | { kind: 'rule' }
    | { kind: 'item'; label: string; keys?: string; go: () => void; off?: boolean }
  > = [
    /*
      A code's content is the thing a customer comes back to this menu for, and
      until now the menu was the one place it was not. Only for a single code:
      two selected have two different values and one panel.
    */
    ...( 1 === selected.length && 'qr' === selected[ 0 ]?.type && onPanel
      ? ( [
          { kind: 'item' as const, label: 'Edit the QR code', go: run( () => onPanel( 'settings' ) ) },
          { kind: 'rule' as const },
        ] )
      : [] ),

    { kind: 'item', label: 'Bring to front', keys: 'Ctrl ]', go: run( () => reorderSelected( 'front' ) ), off: locked },
    { kind: 'item', label: 'Bring forward', keys: ']', go: run( () => reorderSelected( 'up' ) ), off: locked },
    { kind: 'item', label: 'Send backward', keys: '[', go: run( () => reorderSelected( 'down' ) ), off: locked },
    { kind: 'item', label: 'Send to back', keys: 'Ctrl [', go: run( () => reorderSelected( 'back' ) ), off: locked },
    { kind: 'rule' },
    {
      kind: 'item',
      label: 'Group',
      keys: 'Ctrl G',
      go: run( groupSelected ),
      // Grouping one thing is not grouping.
      off: ! many || locked,
    },
    {
      kind: 'item',
      label: 'Ungroup',
      keys: 'Ctrl Shift G',
      go: run( ungroupSelected ),
      off: ! grouped || locked,
    },
    { kind: 'rule' },
    {
      kind: 'item',
      label: 'Rename',
      // One name at a time. Renaming opens the layers list, which is where a
      // name is read back.
      off: many,
      go: run( () => selected[ 0 ] && beginRenaming( selected[ 0 ].id ) ),
    },
    { kind: 'item', label: 'Duplicate', keys: 'Ctrl D', go: run( duplicateSelected ) },
    { kind: 'item', label: locked ? 'Unlock' : 'Lock', go: run( toggleLock ) },
    { kind: 'rule' },
    { kind: 'item', label: 'Delete', keys: 'Del', go: run( deleteSelected ), off: locked },
  ];

  return (
    <div
      className="tb-menu"
      ref={ panel }
      role="menu"
      style={ { left: place.x, top: place.y } }
    >
      <p className="tb-menu__what">
        { many ? `${ selected.length } objects` : selected[ 0 ]?.name || 'Object' }
      </p>

      { items.map( ( item, index ) =>
        item.kind === 'rule' ? (
          <hr className="tb-menu__rule" key={ index } />
        ) : (
          <button
            type="button"
            role="menuitem"
            key={ index }
            className="tb-menu__item"
            disabled={ item.off }
            onClick={ item.go }
          >
            <span>{ item.label }</span>
            { item.keys && <span className="tb-menu__keys">{ item.keys }</span> }
          </button>
        )
      ) }
    </div>
  );
}
